import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UsersIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
  TruckIcon,
  ChartPieIcon,
  SparklesIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  fetchRevenueStats,
  fetchOrderStats,
  fetchTopProducts,
  fetchUserStats,
  fetchPageviewStats,
  fetchSearchTermStats,
  fetchFunnelStats,
  fetchCountryStats,
} from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import { formatMoneyCHF } from '../utils/priceUtils';
import WorldChoropleth from './WorldChoropleth';
import StatListModal from './StatListModal';
import ProductEngagementSection from './statistics/ProductEngagementSection';
import isoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

isoCountries.registerLocale(enLocale);

// ISO-2 (e.g. 'CH') -> flag emoji via regional indicator symbols.
const flagEmoji = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '🏳️';
  const cc = iso2.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '🏳️';
  return String.fromCodePoint(
    ...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65)
  );
};

const PRESETS = [
  { key: '7d', days: 7 },
  { key: '30d', days: 30 },
  { key: '90d', days: 90 },
  { key: 'year', days: 365 },
];

const FUNNEL_COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];

const STATUS_COLORS = {
  paid: '#10b981',
  pending: '#f59e0b',
  failed: '#ef4444',
  voided: '#6b7280',
};

function toLocalDate(d) {
  return d.toISOString().slice(0, 10);
}

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(',')),
  ];
  const blob = new Blob(['﻿' + lines.join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function StatisticsPage() {
  const { t } = useLanguage();

  // Date range state
  const [preset, setPreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const getDateRange = useCallback(() => {
    if (preset === 'custom') {
      return { from: customFrom, to: customTo };
    }
    const p = PRESETS.find((pr) => pr.key === preset);
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (p?.days || 30));
    return { from: toLocalDate(from), to: toLocalDate(to) };
  }, [preset, customFrom, customTo]);

  // Compute the previous equal-length period (e.g. last 30d → prior 30d)
  const getPreviousRange = useCallback(() => {
    const { from, to } = getDateRange();
    if (!from || !to) return { from: '', to: '' };
    const fromD = new Date(from);
    const toD = new Date(to);
    const span = toD - fromD;
    if (!Number.isFinite(span) || span <= 0) return { from: '', to: '' };
    const prevTo = new Date(fromD.getTime() - 24 * 3600 * 1000);
    const prevFrom = new Date(prevTo.getTime() - span);
    return { from: toLocalDate(prevFrom), to: toLocalDate(prevTo) };
  }, [getDateRange]);

  // Data state
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState({
    dataPoints: [],
    totals: { revenue: 0, orders: 0, avgOrderValue: 0 },
  });
  const [orders, setOrders] = useState({
    total: 0,
    byStatus: {},
    byShippingMethod: [],
  });
  const [topProducts, setTopProducts] = useState([]);
  const [users, setUsers] = useState({
    dataPoints: [],
    totals: { totalUsers: 0, newInPeriod: 0 },
  });
  const [pageviews, setPageviews] = useState({ dataPoints: [] });
  const [searchTerms, setSearchTerms] = useState([]);
  // Which "view all" list modal is open: null | 'search' | 'products' | 'countries'
  const [activeModal, setActiveModal] = useState(null);
  const [funnel, setFunnel] = useState([]);

  // Previous-period data (only the metrics we surface as deltas)
  const [prev, setPrev] = useState({
    revenue: 0,
    orders: 0,
    avgOrderValue: 0,
    newUsers: 0,
    pageViews: 0,
  });

  const [countries, setCountries] = useState([]);
  // The engagement section loads its own data; it just follows the range.
  const engagementRange = useMemo(() => getDateRange(), [getDateRange]);
  const [showExportPicker, setShowExportPicker] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { from, to } = getDateRange();
    const granularity =
      preset === '7d' ? 'day' : preset === 'year' ? 'month' : 'day';
    const { from: prevFrom, to: prevTo } = getPreviousRange();

    try {
      const [revRes, ordRes, topRes, usrRes, pvRes, srchRes, funRes, cntRes] =
        await Promise.allSettled([
          fetchRevenueStats(from, to, granularity),
          fetchOrderStats(from, to),
          fetchTopProducts(from, to, 10),
          fetchUserStats(from, to, granularity),
          fetchPageviewStats(from, to, granularity),
          fetchSearchTermStats(from, to, 20),
          fetchFunnelStats(from, to),
          fetchCountryStats(from, to),
        ]);

      if (revRes.status === 'fulfilled' && revRes.value?.data)
        setRevenue(revRes.value.data);
      if (ordRes.status === 'fulfilled' && ordRes.value?.data)
        setOrders(ordRes.value.data);
      if (topRes.status === 'fulfilled' && topRes.value?.data)
        setTopProducts(topRes.value.data.products || []);
      if (usrRes.status === 'fulfilled' && usrRes.value?.data)
        setUsers(usrRes.value.data);
      if (pvRes.status === 'fulfilled' && pvRes.value?.data)
        setPageviews(pvRes.value.data);
      if (srchRes.status === 'fulfilled' && srchRes.value?.data)
        setSearchTerms(srchRes.value.data.terms || []);
      if (funRes.status === 'fulfilled' && funRes.value?.data)
        setFunnel(funRes.value.data.steps || []);
      if (cntRes.status === 'fulfilled' && cntRes.value?.data)
        setCountries(cntRes.value.data.countries || []);

      // Previous period — only the four metrics we display deltas for.
      if (prevFrom && prevTo) {
        const [pRev, pUsr, pPv] = await Promise.allSettled([
          fetchRevenueStats(prevFrom, prevTo, granularity),
          fetchUserStats(prevFrom, prevTo, granularity),
          fetchPageviewStats(prevFrom, prevTo, granularity),
        ]);
        const pRevTotals = pRev.status === 'fulfilled'
          ? pRev.value?.data?.totals
          : null;
        const pUsrTotals = pUsr.status === 'fulfilled'
          ? pUsr.value?.data?.totals
          : null;
        const pPvViews = pPv.status === 'fulfilled'
          ? (pPv.value?.data?.dataPoints || []).reduce(
              (s, d) => s + (d.views || 0),
              0
            )
          : 0;
        setPrev({
          revenue: pRevTotals?.revenue || 0,
          orders: pRevTotals?.orders || 0,
          avgOrderValue: pRevTotals?.avgOrderValue || 0,
          newUsers: pUsrTotals?.newInPeriod || 0,
          pageViews: pPvViews,
        });
      } else {
        setPrev({ revenue: 0, orders: 0, avgOrderValue: 0, newUsers: 0, pageViews: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, getPreviousRange, preset]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const formatCHF = (val) => formatMoneyCHF(val);
  const formatNum = (val) => (val || 0).toLocaleString('de-CH');
  const formatPct = (val) => {
    const n = Number(val) || 0;
    return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
  };

  const computeDelta = (cur, p) => {
    const c = Number(cur) || 0;
    const pp = Number(p) || 0;
    if (pp === 0) return c === 0 ? 0 : null; // null = "n/a" (no baseline)
    return ((c - pp) / pp) * 100;
  };

  // Visitors by country: ranked list (name + flag + share) and a numeric-ISO
  // value map for the choropleth (geoip emits alpha-2, the map keys on numeric).
  const countryData = useMemo(() => {
    const total = countries.reduce((s, c) => s + (c.count || 0), 0);
    const max = countries.reduce((m, c) => Math.max(m, c.count || 0), 0);
    const valueMap = {};
    const numericToName = {};
    const list = countries.map((c) => {
      const isUnknown = !c.country || c.country === 'Unknown';
      const name = isUnknown
        ? t('statistics.countries.unknown')
        : isoCountries.getName(c.country, 'en') || c.country;
      if (!isUnknown) {
        const numeric = isoCountries.alpha2ToNumeric(c.country);
        if (numeric) {
          valueMap[String(parseInt(numeric, 10))] = c.count;
          numericToName[String(parseInt(numeric, 10))] = name;
        }
      }
      return {
        code: isUnknown ? null : c.country,
        name,
        flag: isUnknown ? '🏳️' : flagEmoji(c.country),
        count: c.count || 0,
        share: total > 0 ? ((c.count || 0) / total) * 100 : 0,
      };
    });
    return { list, valueMap, max, numericToName };
  }, [countries, t]);

  // Insights derived from revenue.dataPoints
  const insights = useMemo(() => {
    const dp = revenue.dataPoints || [];
    if (!dp.length) return null;
    let peakRev = dp[0];
    let peakOrd = dp[0];
    let slowest = dp[0];
    dp.forEach((d) => {
      if ((d.revenue || 0) > (peakRev.revenue || 0)) peakRev = d;
      if ((d.orderCount || 0) > (peakOrd.orderCount || 0)) peakOrd = d;
      if ((d.revenue || 0) < (slowest.revenue || 0)) slowest = d;
    });
    return { peakRev, peakOrd, slowest };
  }, [revenue.dataPoints]);

  // Order status pie data
  const statusPieData = useMemo(() => {
    const bs = orders.byStatus || {};
    return Object.keys(bs)
      .filter((k) => bs[k] > 0)
      .map((k) => ({ name: k, value: bs[k] }));
  }, [orders.byStatus]);

  // Page-views totals
  const pageViewsTotal = useMemo(
    () =>
      (pageviews.dataPoints || []).reduce(
        (s, d) => s + (d.views || 0),
        0
      ),
    [pageviews.dataPoints]
  );
  const sessionsTotal = useMemo(
    () =>
      (pageviews.dataPoints || []).reduce(
        (s, d) => s + (d.uniqueSessions || 0),
        0
      ),
    [pageviews.dataPoints]
  );

  // Total revenue (for top product share calc)
  const topProductsWithShare = useMemo(() => {
    const total = revenue.totals.revenue || 0;
    return topProducts.map((p) => ({
      ...p,
      share: total > 0 ? ((p.revenue || 0) / total) * 100 : 0,
    }));
  }, [topProducts, revenue.totals.revenue]);

  // CSV export handlers
  const exportRevenueCsv = () => {
    const rows = (revenue.dataPoints || []).map((d) => ({
      date: d.date,
      revenue: d.revenue || 0,
      orderCount: d.orderCount || 0,
    }));
    downloadCsv(`revenue-${getDateRange().from}-to-${getDateRange().to}.csv`, rows);
    setShowExportPicker(false);
  };
  /**
   * Professional XLSX export for top products.
   *
   * Builds one styled worksheet with:
   *   - A title row + the date range
   *   - A clearly labelled header row with proper Excel column widths
   *   - One row per product enriched with HCI catalogue data (pharmacode,
   *     brand, company, ATC, supply category, GTIN, base price …) — the
   *     enrichment is computed server-side; here we just lay it out
   *   - A totals row summarising units sold and revenue
   */
  const exportTopProductsXlsx = () => {
    if (!topProductsWithShare.length) return;

    const range = getDateRange();
    const wb = XLSX.utils.book_new();

    // Header order also determines column order in the file.
    const columns = [
      { key: 'rank',               header: 'Rank',                  width: 6  },
      { key: 'pharmacode',         header: 'Pharmacode',            width: 14 },
      { key: 'name',               header: 'Product name',          width: 42 },
      { key: 'brand',              header: 'Brand',                 width: 22 },
      { key: 'company',            header: 'Company',               width: 28 },
      { key: 'supplyCategory',     header: 'Supply category',       width: 8  },
      { key: 'categoryType',       header: 'Category',              width: 14 },
      { key: 'atcCode',            header: 'ATC code',              width: 12 },
      { key: 'atcDescription',     header: 'ATC description',       width: 32 },
      { key: 'itCode',             header: 'IT code',               width: 10 },
      { key: 'gtin',               header: 'GTIN',                  width: 16 },
      { key: 'swissMedicNumber',   header: 'SwissMedic #',          width: 14 },
      { key: 'packageDescription', header: 'Package',               width: 18 },
      { key: 'totalSold',          header: 'Units sold',            width: 11, kind: 'int' },
      { key: 'orderCount',         header: 'Orders',                width: 9,  kind: 'int' },
      { key: 'revenue',            header: 'Revenue (CHF)',         width: 14, kind: 'money' },
      { key: 'avgUnitPrice',       header: 'Avg unit price (CHF)',  width: 18, kind: 'money' },
      { key: 'basePrice',          header: 'Base price (CHF)',      width: 16, kind: 'money' },
      { key: 'share',              header: 'Share of revenue',      width: 16, kind: 'percent' },
    ];

    const titleRow = ['Top products report'];
    const subtitleRow = [`${range.from}  →  ${range.to}`];
    const blankRow = [''];
    const headerRow = columns.map((c) => c.header);

    const dataRows = topProductsWithShare.map((p, i) => columns.map((c) => {
      if (c.key === 'rank') return i + 1;
      if (c.key === 'share') return (p.share || 0) / 100; // store as 0-1, format as %
      const v = p[c.key];
      if (v == null || v === '') return '';
      if (c.kind === 'int') return Number(v);
      if (c.kind === 'money') return Number(v);
      return v;
    }));

    // Totals row at the bottom (only sums the numeric columns where it
    // makes sense — share % can be re-summed; avg unit price cannot).
    const totalUnits = topProductsWithShare.reduce((s, p) => s + (p.totalSold || 0), 0);
    const totalOrders = topProductsWithShare.reduce((s, p) => s + (p.orderCount || 0), 0);
    const totalRevenue = topProductsWithShare.reduce((s, p) => s + (p.revenue || 0), 0);
    const totalShare = topProductsWithShare.reduce((s, p) => s + (p.share || 0), 0) / 100;
    const totalsRow = columns.map((c) => {
      if (c.key === 'rank') return 'TOTAL';
      if (c.key === 'totalSold') return totalUnits;
      if (c.key === 'orderCount') return totalOrders;
      if (c.key === 'revenue') return Number(totalRevenue.toFixed(2));
      if (c.key === 'share') return totalShare;
      return '';
    });

    const aoa = [titleRow, subtitleRow, blankRow, headerRow, ...dataRows, totalsRow];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Column widths
    ws['!cols'] = columns.map((c) => ({ wch: c.width }));

    // Merge the title and subtitle across all columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } },
    ];

    // Cell formatting — numbers and percentages. SheetJS Community Edition
    // can't write fills/borders, but `z` (number format) is fully supported
    // and Excel renders these cleanly.
    const dataStart = 4; // row index (0-based) where data rows begin
    const totalsRowIdx = dataStart + dataRows.length;
    columns.forEach((c, colIdx) => {
      const colLetter = XLSX.utils.encode_col(colIdx);
      // Apply to data rows + totals row.
      for (let r = dataStart; r <= totalsRowIdx; r++) {
        const cell = ws[`${colLetter}${r + 1}`];
        if (!cell) continue;
        if (c.kind === 'money') cell.z = '#,##0.00';
        else if (c.kind === 'percent') cell.z = '0.00%';
        else if (c.kind === 'int') cell.z = '#,##0';
      }
      // Header cell gets bold (note format).
      const headerCell = ws[`${colLetter}${dataStart}`];
      if (headerCell) headerCell.s = { font: { bold: true } };
    });

    // Title cell — bold, larger.
    const titleCell = ws['A1'];
    if (titleCell) titleCell.s = { font: { bold: true, sz: 16 } };

    XLSX.utils.book_append_sheet(wb, ws, 'Top products');

    XLSX.writeFile(wb, `top-products-${range.from}-to-${range.to}.xlsx`, {
      bookType: 'xlsx',
      compression: true,
    });
    setShowExportPicker(false);
  };
  const exportSearchTermsCsv = () => {
    const rows = searchTerms.map((s, i) => ({
      rank: i + 1,
      query: s.query,
      count: s.count,
    }));
    downloadCsv(
      `search-terms-${getDateRange().from}-to-${getDateRange().to}.csv`,
      rows
    );
    setShowExportPicker(false);
  };

  // Sparkline component
  const Sparkline = ({ data, dataKey, color }) => {
    if (!data || data.length < 2) return null;
    return (
      <ResponsiveContainer width="100%" height={36}>
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spark-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spark-${dataKey})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const DeltaBadge = ({ value }) => {
    if (value === null || value === undefined) return null;
    if (!Number.isFinite(value)) return null;
    const positive = value > 0;
    const negative = value < 0;
    const cls = positive
      ? 'text-green-700 bg-green-50'
      : negative
      ? 'text-red-700 bg-red-50'
      : 'text-gray-600 bg-gray-100';
    const Icon = positive ? ArrowUpIcon : negative ? ArrowDownIcon : null;
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}
        title={t('statistics.vsPrev')}
      >
        {Icon && <Icon className="h-2.5 w-2.5" />}
        {formatPct(value)}
      </span>
    );
  };

  // KPI card with sparkline + delta
  const StatCard = ({
    icon: Icon,
    label,
    value,
    sub,
    color,
    sparkData,
    sparkKey,
    sparkColor,
    delta,
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-gray-500">{label}</span>
        </div>
        <DeltaBadge value={delta} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      {sparkData && sparkData.length > 1 && (
        <div className="mt-2 -mx-2">
          <Sparkline
            data={sparkData}
            dataKey={sparkKey}
            color={sparkColor || '#6366f1'}
          />
        </div>
      )}
    </motion.div>
  );

  const InsightCard = ({ icon: Icon, label, day, valueLabel, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-start gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-900 truncate">
          {day || t('statistics.insights.noActivity')}
        </p>
        <p className="text-xs text-gray-400 truncate">{valueLabel}</p>
      </div>
    </div>
  );

  // Config for the active "view all" list modal. Each section supplies the same
  // uniform interface (loadPage / renderRow / csvRow); search terms and top
  // products page from the server, countries slice the already-loaded array.
  // Kept in a memo so loadPage keeps a stable identity while the modal is open
  // (otherwise the modal would refetch on every parent re-render).
  const rankBadge = (rank) => (
    <span className="text-xs font-bold text-gray-400 w-8 shrink-0 tabular-nums">
      {rank}
    </span>
  );
  const csvSlug = (q) => (q ? '-' + q.replace(/[^a-z0-9]+/gi, '_') : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statModalConfig = useMemo(() => {
    const { from, to } = getDateRange();
    const totalRevenue = revenue.totals.revenue || 0;

    if (activeModal === 'search') {
      return {
        title: t('statistics.tables.topSearchTerms'),
        icon: <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />,
        filterable: true,
        filterPlaceholder: t('statistics.listModal.filterSearchTerms'),
        loadPage: async (offset, limit, q) => {
          const res = await fetchSearchTermStats(from, to, limit, offset, q);
          return { items: res?.data?.terms || [], total: res?.data?.total || 0 };
        },
        renderRow: (s, rank) => (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {rankBadge(rank)}
              <span className="text-sm text-gray-700 truncate">"{s.query}"</span>
            </div>
            <span className="text-sm font-semibold text-gray-600 tabular-nums shrink-0">
              {formatNum(s.count)}
            </span>
          </div>
        ),
        csvRow: (s, rank) => ({ rank, query: s.query, count: s.count }),
        csvFilename: (q) => `search-terms${csvSlug(q)}-${from}-to-${to}.csv`,
      };
    }

    if (activeModal === 'products') {
      return {
        title: t('statistics.tables.topProducts'),
        icon: <ArrowTrendingUpIcon className="h-5 w-5 text-gray-500" />,
        filterable: true,
        filterPlaceholder: t('statistics.listModal.filterProducts'),
        loadPage: async (offset, limit, q) => {
          const res = await fetchTopProducts(from, to, limit, offset, q);
          return {
            items: res?.data?.products || [],
            total: res?.data?.total || 0,
          };
        },
        renderRow: (p, rank) => {
          const share = totalRevenue > 0 ? (p.revenue / totalRevenue) * 100 : 0;
          return (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {rankBadge(rank)}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-700 truncate">{p.name}</p>
                  <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500"
                      style={{ width: `${Math.min(share, 100).toFixed(1)}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <span className="text-sm font-semibold text-gray-900">
                  {formatCHF(p.revenue)}
                </span>
                <span className="text-[10px] text-gray-500">
                  {p.totalSold} {t('statistics.tables.sold')} · {share.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        },
        csvRow: (p, rank) => ({
          rank,
          name: p.name,
          units_sold: p.totalSold,
          orders: p.orderCount,
          revenue: p.revenue,
          share_pct:
            totalRevenue > 0
              ? Number(((p.revenue / totalRevenue) * 100).toFixed(2))
              : 0,
        }),
        csvFilename: (q) => `top-products${csvSlug(q)}-${from}-to-${to}.csv`,
      };
    }

    if (activeModal === 'countries') {
      const all = countryData.list;
      return {
        title: t('statistics.tables.topCountries'),
        icon: <GlobeAltIcon className="h-5 w-5 text-gray-500" />,
        filterable: true,
        filterPlaceholder: t('statistics.listModal.filterCountries'),
        // In-memory source: the full country set is already loaded, so filter
        // and slice client-side behind the same loadPage interface.
        loadPage: async (offset, limit, q) => {
          const needle = q.toLowerCase();
          const filtered = needle
            ? all.filter((c) => c.name.toLowerCase().includes(needle))
            : all;
          return {
            items: filtered.slice(offset, offset + limit),
            total: filtered.length,
          };
        },
        renderRow: (c, rank) => (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {rankBadge(rank)}
              <span className="text-base leading-none">{c.flag}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-700 truncate">{c.name}</p>
                <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500"
                    style={{ width: `${Math.min(c.share, 100).toFixed(1)}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end shrink-0">
              <span className="text-sm font-semibold text-gray-900">
                {formatNum(c.count)}
              </span>
              <span className="text-[10px] text-gray-500">
                {c.share.toFixed(1)}%
              </span>
            </div>
          </div>
        ),
        csvRow: (c, rank) => ({
          rank,
          country: c.name,
          code: c.code || '',
          sessions: c.count,
          share_pct: Number(c.share.toFixed(2)),
        }),
        csvFilename: (q) => `top-countries${csvSlug(q)}-${from}-to-${to}.csv`,
      };
    }

    return null;
  }, [activeModal, getDateRange, revenue.totals.revenue, countryData, t]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('statistics.title')}
          </h1>
          <p className="text-sm text-gray-500">{t('statistics.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                preset === p.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t(`statistics.presets.${p.key}`)}
            </button>
          ))}
          <button
            onClick={() => setPreset('custom')}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors flex items-center gap-1 ${
              preset === 'custom'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <CalendarIcon className="h-4 w-4" />
            {t('statistics.presets.custom')}
          </button>

          {/* CSV export dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportPicker((s) => !s)}
              className="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {t('statistics.exportCsv')}
            </button>
            {showExportPicker && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={exportRevenueCsv}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 first:rounded-t-lg"
                >
                  {t('statistics.exportRevenue')}
                </button>
                <button
                  onClick={exportTopProductsXlsx}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {t('statistics.exportTopProducts')}
                </button>
                <button
                  onClick={exportSearchTermsCsv}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 last:rounded-b-lg"
                >
                  {t('statistics.exportSearchTerms')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {preset === 'custom' && (
        <div className="flex items-center gap-3 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          />
          <span className="text-gray-400">{t('statistics.to')}</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          />
          <button
            onClick={fetchAll}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {t('statistics.apply')}
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      )}

      {!loading && (
        <>
          {/* Overview KPI cards with sparklines + period-over-period deltas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={CurrencyDollarIcon}
              label={t('statistics.cards.revenue')}
              value={formatCHF(revenue.totals.revenue)}
              sub={`${formatNum(revenue.totals.orders)} ${t('statistics.cards.orders')}`}
              color="bg-green-500"
              sparkData={revenue.dataPoints}
              sparkKey="revenue"
              sparkColor="#10b981"
              delta={computeDelta(revenue.totals.revenue, prev.revenue)}
            />
            <StatCard
              icon={ShoppingCartIcon}
              label={t('statistics.cards.avgOrder')}
              value={formatCHF(revenue.totals.avgOrderValue)}
              color="bg-blue-500"
              sparkData={revenue.dataPoints}
              sparkKey="orderCount"
              sparkColor="#3b82f6"
              delta={computeDelta(
                revenue.totals.avgOrderValue,
                prev.avgOrderValue
              )}
            />
            <StatCard
              icon={UsersIcon}
              label={t('statistics.cards.newUsers')}
              value={formatNum(users.totals.newInPeriod)}
              sub={`${formatNum(users.totals.totalUsers)} ${t('statistics.cards.totalUsers')}`}
              color="bg-purple-500"
              sparkData={users.dataPoints}
              sparkKey="newUsers"
              sparkColor="#8b5cf6"
              delta={computeDelta(users.totals.newInPeriod, prev.newUsers)}
            />
            <StatCard
              icon={EyeIcon}
              label={t('statistics.cards.pageViews')}
              value={formatNum(pageViewsTotal)}
              sub={`${formatNum(sessionsTotal)} ${t('statistics.cards.sessions')}`}
              color="bg-amber-500"
              sparkData={pageviews.dataPoints}
              sparkKey="views"
              sparkColor="#f59e0b"
              delta={computeDelta(pageViewsTotal, prev.pageViews)}
            />
          </div>

          {/* Highlights / insight cards */}
          {insights && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InsightCard
                icon={SparklesIcon}
                label={t('statistics.insights.peakRevenue')}
                day={insights.peakRev?.date}
                valueLabel={formatCHF(insights.peakRev?.revenue)}
                color="bg-green-500"
              />
              <InsightCard
                icon={ShoppingCartIcon}
                label={t('statistics.insights.peakOrders')}
                day={insights.peakOrd?.date}
                valueLabel={`${formatNum(
                  insights.peakOrd?.orderCount
                )} ${t('statistics.cards.orders')}`}
                color="bg-indigo-500"
              />
              <InsightCard
                icon={ArrowTrendingUpIcon}
                label={t('statistics.insights.slowestDay')}
                day={insights.slowest?.date}
                valueLabel={formatCHF(insights.slowest?.revenue)}
                color="bg-gray-400"
              />
            </div>
          )}

          {/* Revenue + Orders charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                {t('statistics.charts.revenueOverTime')}
              </h3>
              {(revenue.dataPoints || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={revenue.dataPoints}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatCHF(v)} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                {t('statistics.charts.ordersOverTime')}
              </h3>
              {(revenue.dataPoints || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={revenue.dataPoints}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="orderCount"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>
          </div>

          {/* New users + page views charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <UsersIcon className="h-4 w-4" />
                {t('statistics.charts.usersOverTime')}
              </h3>
              {(users.dataPoints || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={users.dataPoints}>
                    <defs>
                      <linearGradient
                        id="users-grad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#users-grad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <EyeIcon className="h-4 w-4" />
                {t('statistics.charts.pageviewsOverTime')}
              </h3>
              {(pageviews.dataPoints || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={pageviews.dataPoints}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="views"
                      name={t('statistics.cards.pageViews')}
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="uniqueSessions"
                      name={t('statistics.cards.sessions')}
                      stroke="#06b6d4"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>
          </div>

          {/* Order status donut + shipping methods bar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <ChartPieIcon className="h-4 w-4" />
                {t('statistics.charts.ordersByStatus')}
              </h3>
              {statusPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {statusPieData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={STATUS_COLORS[entry.name] || '#9ca3af'}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        formatNum(value),
                        t(`statistics.status.${name}`) || name,
                      ]}
                    />
                    <Legend
                      formatter={(name) =>
                        t(`statistics.status.${name}`) || name
                      }
                      wrapperStyle={{ fontSize: 11 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <TruckIcon className="h-4 w-4" />
                {t('statistics.charts.shippingMethods')}
              </h3>
              {(orders.byShippingMethod || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={orders.byShippingMethod}
                    layout="vertical"
                    margin={{ left: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="method"
                      type="category"
                      tick={{ fontSize: 11 }}
                      width={120}
                    />
                    <Tooltip formatter={(v) => formatNum(v)} />
                    <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>
          </div>

          {/* Visitors by country */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <GlobeAltIcon className="h-4 w-4" />
                {t('statistics.charts.visitorsByCountry')}
              </h3>
              {countryData.list.length > 0 ? (
                <WorldChoropleth
                  values={countryData.valueMap}
                  max={countryData.max}
                  nameFor={(id) => countryData.numericToName[id]}
                />
              ) : (
                <p className="text-sm text-gray-400 py-12 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <GlobeAltIcon className="h-4 w-4" />
                  {t('statistics.tables.topCountries')}
                </h3>
                {countryData.list.length > 10 && (
                  <button
                    onClick={() => setActiveModal('countries')}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    {t('statistics.viewAll')}
                  </button>
                )}
              </div>
              {countryData.list.length > 0 ? (
                <div className="space-y-3">
                  {countryData.list.slice(0, 10).map((c, i) => (
                    <div
                      key={c.code || `unknown-${i}`}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-base leading-none">{c.flag}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-700 truncate">
                            {c.name}
                          </p>
                          <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500"
                              style={{
                                width: `${Math.min(c.share, 100).toFixed(1)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        <span className="text-sm font-semibold text-gray-900">
                          {formatNum(c.count)}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {c.share.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>
          </div>

          {/* Top products + Search terms */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <ArrowTrendingUpIcon className="h-4 w-4" />
                  {t('statistics.tables.topProducts')}
                </h3>
                {topProductsWithShare.length > 0 && (
                  <button
                    onClick={() => setActiveModal('products')}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    {t('statistics.viewAll')}
                  </button>
                )}
              </div>
              {topProductsWithShare.length > 0 ? (
                <div className="space-y-3">
                  {topProductsWithShare.map((p, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="text-xs font-bold text-gray-400 w-5">
                          {i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-700 truncate">
                            {p.name}
                          </p>
                          <div className="mt-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-indigo-500"
                              style={{
                                width: `${Math.min(p.share, 100).toFixed(1)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCHF(p.revenue)}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {p.totalSold} {t('statistics.tables.sold')} ·{' '}
                          {p.share.toFixed(1)}% {t('statistics.shareOfRevenue')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  {t('statistics.tables.topSearchTerms')}
                </h3>
                {searchTerms.length > 0 && (
                  <button
                    onClick={() => setActiveModal('search')}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                  >
                    {t('statistics.viewAll')}
                  </button>
                )}
              </div>
              {searchTerms.length > 0 ? (
                <div className="space-y-3">
                  {searchTerms.slice(0, 10).map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-400 w-5">
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-700">
                          "{s.query}"
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-600">
                        {formatNum(s.count)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 py-8 text-center">
                  {t('statistics.noData')}
                </p>
              )}
            </div>
          </div>

          <StatListModal
            open={activeModal !== null}
            onClose={() => setActiveModal(null)}
            {...(statModalConfig || {})}
          />

          {/* Conversion funnel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FunnelIcon className="h-4 w-4" />
              {t('statistics.charts.conversionFunnel')}
            </h3>
            {funnel.length > 0 ? (
              <div className="space-y-3">
                {funnel.map((step, i) => {
                  const maxCount = funnel[0]?.count || 1;
                  const widthPct = Math.max(
                    (step.count / maxCount) * 100,
                    4
                  );
                  return (
                    <div
                      key={step.name}
                      className="flex items-center gap-4"
                    >
                      <span className="text-xs text-gray-500 w-36 shrink-0">
                        {t(`statistics.funnel.${step.name}`)}
                      </span>
                      <div className="flex-1 bg-gray-100 rounded-full h-7 relative">
                        <div
                          className="h-7 rounded-full flex items-center px-3 transition-all duration-500"
                          style={{
                            width: `${widthPct}%`,
                            backgroundColor:
                              FUNNEL_COLORS[i] || '#6366f1',
                          }}
                        >
                          <span className="text-xs font-semibold text-white whitespace-nowrap">
                            {formatNum(step.count)}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 w-16 text-right shrink-0">
                        {step.conversionRate}%
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">
                {t('statistics.noData')}
              </p>
            )}
          </div>
        </>
      )}

      {/* Product engagement — stays mounted across range changes so its
          drill-down filters survive; hidden while the page-level data loads. */}
      {engagementRange.from && engagementRange.to && (
        <div className={loading ? 'hidden' : ''}>
          <ProductEngagementSection from={engagementRange.from} to={engagementRange.to} />
        </div>
      )}
    </div>
  );
}
